import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
	Activity,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	ChevronDown,
	ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { adminApiUrl } from "@/config";

// --- Types (mirror cronHealth.service.ts) ---

interface OverdueOrg {
	orgId: string;
	orgName: string;
	lastSuccessAt: string | null;
	state: "stale" | "never-ran";
}

interface CronJobHealth {
	jobName: string;
	pattern: string;
	cadence: string;
	scheduleType: "fixed" | "per-org-daily" | "unknown";
	enabled: boolean;
	registered: boolean;
	lastTickAt: string | null;
	expectedNextTickBy: string | null;
	tickOverdue: boolean;
	eligibleOrgCount: number | null;
	overdueOrgCount: number | null;
	overdueOrgs: OverdueOrg[];
	healthy: boolean;
}

// --- Helpers ---

function timeAgo(date: string | null): string {
	if (!date) return "never";
	const diff = Date.now() - new Date(date).getTime();
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "just now";
	if (mins < 60) return `${mins}m ago`;
	const hours = Math.floor(mins / 60);
	if (hours < 24) return `${hours}h ago`;
	return `${Math.floor(hours / 24)}d ago`;
}

async function fetchCronHealth(): Promise<CronJobHealth[]> {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl("/job-monitor/cron-health"), {
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	return data.data ?? [];
}

// --- Card ---

function JobCard({ job }: { job: CronJobHealth }) {
	const [expanded, setExpanded] = useState(false);
	const hasOverdueOrgs = (job.overdueOrgCount ?? 0) > 0;

	const tone = !job.enabled
		? "border-gray-300"
		: job.healthy
			? "border-green-200"
			: "border-red-300";

	const icon = !job.enabled ? (
		<XCircle className="w-4 h-4 text-gray-400" />
	) : job.healthy ? (
		<CheckCircle2 className="w-4 h-4 text-green-500" />
	) : (
		<AlertTriangle className="w-4 h-4 text-red-500" />
	);

	return (
		<Card className={cn("border-2", tone)}>
			<CardHeader className="pb-2">
				<CardTitle className="flex items-center justify-between text-sm">
					<span className="truncate">{job.jobName}</span>
					{icon}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-2">
				<div className="flex flex-wrap gap-1">
					<Badge className="bg-gray-100 text-gray-700">{job.cadence}</Badge>
					{job.scheduleType === "per-org-daily" && (
						<Badge className="bg-indigo-100 text-indigo-700">per-org</Badge>
					)}
					{!job.enabled && (
						<Badge className="bg-gray-200 text-gray-600">disabled</Badge>
					)}
					{!job.registered && job.enabled && (
						<Badge className="bg-red-100 text-red-700">not registered</Badge>
					)}
				</div>

				<p
					className={cn(
						"text-xs",
						job.tickOverdue ? "text-red-600 font-medium" : "text-gray-500",
					)}
				>
					Last tick: {timeAgo(job.lastTickAt)}
					{job.tickOverdue && " — OVERDUE"}
				</p>

				{job.scheduleType === "per-org-daily" &&
					job.eligibleOrgCount != null && (
						<div className="text-xs">
							{hasOverdueOrgs ? (
								<button
									type="button"
									className="flex items-center gap-1 text-red-600 font-medium"
									onClick={() => setExpanded((v) => !v)}
								>
									{expanded ? (
										<ChevronDown className="w-3 h-3" />
									) : (
										<ChevronRight className="w-3 h-3" />
									)}
									{job.overdueOrgCount}/{job.eligibleOrgCount} orgs overdue
								</button>
							) : (
								<span className="text-green-600">
									{job.eligibleOrgCount} orgs on time
								</span>
							)}

							{expanded && hasOverdueOrgs && (
								<div className="mt-2 border rounded max-h-48 overflow-y-auto divide-y">
									{job.overdueOrgs.map((o) => (
										<div
											key={o.orgId}
											className="flex items-center justify-between px-2 py-1"
										>
											<span className="truncate mr-2">{o.orgName}</span>
											<Badge
												className={
													o.state === "never-ran"
														? "bg-red-100 text-red-700"
														: "bg-yellow-100 text-yellow-700"
												}
											>
												{o.state === "never-ran"
													? "never ran"
													: timeAgo(o.lastSuccessAt)}
											</Badge>
										</div>
									))}
								</div>
							)}
						</div>
					)}
			</CardContent>
		</Card>
	);
}

// --- Board ---

export function CronHealthBoard() {
	const { data, isLoading } = useQuery({
		queryKey: ["job-monitor-cron-health"],
		queryFn: fetchCronHealth,
		refetchInterval: 15000,
	});

	const jobs = data ?? [];
	const unhealthy = jobs.filter((j) => j.enabled && !j.healthy).length;

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2 text-lg">
					<Activity className="w-5 h-5" />
					Scheduler Health
					{unhealthy > 0 ? (
						<Badge className="bg-red-100 text-red-700">
							{unhealthy} need attention
						</Badge>
					) : (
						jobs.length > 0 && (
							<Badge className="bg-green-100 text-green-700">all healthy</Badge>
						)
					)}
				</CardTitle>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<p className="text-gray-500 text-center py-4">Loading…</p>
				) : jobs.length === 0 ? (
					<p className="text-gray-500 text-center py-4">No scheduled jobs</p>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
						{jobs.map((job) => (
							<JobCard key={job.jobName} job={job} />
						))}
					</div>
				)}
			</CardContent>
		</Card>
	);
}
